import React, { useState, useEffect, useMemo } from 'react';
import axios from 'axios';
import API_BASE_URL from './config';
import './css/AdminAIDashboard.css';

const AdminAIDashboard = () => {
  const [leaders, setLeaders] = useState([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedLeader, setSelectedLeader] = useState(null);
  const [formResponses, setFormResponses] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [conversation, setConversation] = useState([]);
  const [question, setQuestion] = useState('');
  const [initialAnalysis, setInitialAnalysis] = useState(null);

  useEffect(() => {
    (async () => {
      try {
        const resp = await axios.get(`${API_BASE_URL}/api/customers`, { withCredentials: true });
        setLeaders(resp.data.data);
      } catch (err) {
        setError('Failed to load leaders.');
      }
    })();
  }, []);

  const filteredLeaders = useMemo(() => {
    const lowerSearch = searchTerm.toLowerCase();
    const result = leaders.filter((leader) => {
      const combined = `${leader.firstName || ''} ${leader.lastName || ''} ${leader.email || ''} ${leader.organization || ''}`.toLowerCase();
      return combined.includes(lowerSearch);
    });
    result.sort((a, b) => (b.completeness || 0) - (a.completeness || 0));
    return result;
  }, [leaders, searchTerm]);

  const getCompletenessColor = (value) => {
    if (value >= 60) return 'hot';
    if (value >= 45) return 'medium';
    return 'low';
  };

  const handleSelectLeader = async (leader) => {
    setLoading(true);
    setError(null);
    setInitialAnalysis(null);
    setConversation([]);
    setQuestion('');
    setFormResponses([]);
    setSelectedLeader(null);

    try {
      const response = await axios.get(`${API_BASE_URL}/api/customer-visual-report/${leader._id}`, { withCredentials: true });
      const apiData = response.data.data;
      if (!apiData.formResponses || apiData.formResponses.length === 0) {
        setError('No feedback data available for this leader.');
        setLoading(false);
        return;
      }
      const productId = apiData.customer.productDetails ? apiData.customer.productDetails._id : null;
      console.log('Product ID:', productId || 'N/A');
      console.log('Form Responses:', apiData.formResponses); // Log to verify data
      const newSelectedLeader = {
        ...apiData.customer,
        productId,
        productName: apiData.customer.productDetails ? apiData.customer.productDetails.productName : 'N/A'
      };
      console.log('Selected Leader Data:', newSelectedLeader);
      setSelectedLeader(newSelectedLeader);
      setFormResponses(apiData.formResponses);
      try {
        const analysis = await askAiAnalysis(
          apiData.formResponses,
          "Provide a detailed analysis of this leader's 360-degree feedback, including strengths, areas for improvement, and any notable trends.",
          productId
        );
        setInitialAnalysis(analysis.response);
      } catch (e) {
        console.error('Failed to fetch AI analysis:', e);
        setError('Failed to fetch AI analysis: ' + e.message);
      }
    } catch (err) {
      setError(`Failed to analyze leader feedback: ${err.message}`);
    } finally {
      setLoading(false);
    }
  };

  const askAiAnalysis = async (responses, query, productId, comparisonType = null) => {
    if (!responses || !Array.isArray(responses)) {
      throw new Error('Feedback data is missing or invalid.');
    }
    let messages;
    if (typeof query === 'string') {
      messages = [{
        role: 'user',
        content: JSON.stringify({ data: responses, productId, question: query, comparisonType })
      }];
    } else if (typeof query === 'object' && query.analysisType) {
      messages = [{
        role: 'user',
        content: JSON.stringify({ data: responses, productId, analysisType: query.analysisType, comparisonType })
      }];
    } else {
      throw new Error('Invalid query format.');
    }
    const resp = await axios.post(`${API_BASE_URL}/api/ask-ai`, { messages }, { withCredentials: true });
    console.log('AI Response:', resp.data); // Log to verify response
    return resp.data;
  };

  const handleAskAi = async (suggestedQuestion = null, preset = null, comparisonType = null) => {
    if (!selectedLeader || formResponses.length === 0) return;
    let query;
    if (preset) {
      query = { analysisType: preset };
    } else {
      query = suggestedQuestion || question.trim();
      if (!query) return;
    }
    setLoading(true);
    try {
      const analysis = await askAiAnalysis(formResponses, query, selectedLeader.productId, comparisonType);
      setConversation((prev) => [...prev, { question: preset || query, answer: analysis.response }]);
    } catch (e) {
      console.error('Failed to ask AI:', e);
      setError('Failed to ask AI: ' + e.message);
    } finally {
      setLoading(false);
    }
    if (!suggestedQuestion && !preset) setQuestion('');
  };

  const handleClearChat = () => {
    setConversation([]);
    setError(null);
  };

  const renderAnalysis = (answer) => {
    if (typeof answer === 'string') {
      return <div>{answer}</div>;
    } else if (typeof answer === 'object' && answer.error) {
      return (
        <div>
          <strong>Error:</strong> {answer.error}
          {answer.raw && (
            <div>
              <strong>Raw Response:</strong>
              <pre>{answer.raw}</pre>
            </div>
          )}
        </div>
      );
    } else if (typeof answer === 'object') {
      return (
        <div>
          {answer.inconsistencies && (
            <>
              <h3>Inconsistencies in Self vs Rater Responses</h3>
              <ul>
                {answer.inconsistencies.map((item, idx) => (
                  <li key={idx}>
                    {item.behaviorDescription}: Self rated {item.selfRating} (
                    {item.selfHigher ? 'higher' : 'lower'} by {item.difference.toFixed(2)})
                  </li>
                ))}
              </ul>
            </>
          )}
          {answer.guidance && (
            <>
              <h3>Actionable Guidance</h3>
              <ul>
                {answer.guidance.map((item, idx) => (
                  <li key={idx}>{item.behaviorDescription}: {item.advice}</li>
                ))}
              </ul>
            </>
          )}
        </div>
      );
    } else {
      return <div>{JSON.stringify(answer)}</div>;
    }
  };

  return (
    <div className="admin-ai-dashboard-container">
      <h2>AI Leadership Insights (via Customer Visual Report Data)</h2>
      <div className="ai-dashboard-layout">
        <div className="leader-selection-panel">
          <div className="search-bar-container">
            <input
              className="search-bar"
              type="text"
              placeholder="Search leaders..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
            />
          </div>
          {error && <div className="error-message">{error}</div>}
          <ul className="leader-list">
            {filteredLeaders.map((leader) => (
              <li
                key={leader._id}
                className={selectedLeader?._id === leader._id ? 'active-leader' : ''}
                onClick={() => handleSelectLeader(leader)}
                title={leader.completeness === 0 ? '0% readiness - no data to analyze' : 'Click to analyze'}
                style={{ cursor: leader.completeness === 0 ? 'not-allowed' : 'pointer' }}
              >
                <strong>{leader.firstName} {leader.lastName}</strong><br />
                <span className="leader-email">{leader.email}</span><br />
                <span className="leader-org">{leader.organization}</span>
                <div className="readiness-indicator">
                  <div className={`bar ${getCompletenessColor(leader.completeness || 0)}`} style={{ width: `${leader.completeness || 0}%` }}></div>
                  <span>{leader.completeness || 0}%</span>
                </div>
              </li>
            ))}
          </ul>
        </div>
        <div className="ai-chat-panel">
          {loading && <div className="loading-overlay">Loading...</div>}
          {selectedLeader ? (
            <>
              <div className="leader-header">
                <h3>Analyzing: {selectedLeader.firstName} {selectedLeader.lastName}</h3>
                <p><strong>Email:</strong> {selectedLeader.email}</p>
                <p><strong>Org:</strong> {selectedLeader.organization} | <strong>Product:</strong> {selectedLeader.productName}</p>
              </div>
              <div className="preset-buttons">
                <button onClick={() => handleAskAi(null, 'selfAssessmentAnalysis')}>Self-Assessment Analysis</button>
              </div>
              {initialAnalysis && (
                <div className="initial-analysis">
                  <h4>Initial Analysis</h4>
                  {renderAnalysis(initialAnalysis)}
                </div>
              )}
              <div className="suggested-questions">
                <h4>Suggested Questions</h4>
                <button onClick={() => handleAskAi("What are the leader's top strengths?")}>Analyze Strengths</button>
                <button onClick={() => handleAskAi("What are the main areas for improvement?")}>Identify Weaknesses</button>
                <button onClick={() => handleAskAi("How does this leader compare to others in the same product?", null, 'sameProduct')}>
                  Compare to Peers in Same Product
                </button>
              </div>
              <div className="conversation-container">
                {conversation.length === 0 ? (
                  <div className="no-convo-message"><p>Ask additional questions below.</p></div>
                ) : (
                  conversation.map((item, idx) => (
                    <div key={idx} className="chat-pair">
                      <div className="chat-question">
                        <strong>You:</strong> {typeof item.question === 'string' ? item.question : 'Self-Assessment Analysis'}
                      </div>
                      <div className="chat-answer"><strong>AI:</strong> {renderAnalysis(item.answer)}</div>
                    </div>
                  ))
                )}
              </div>
              <div className="chat-input-section">
                <textarea
                  rows={3}
                  placeholder="Ask something else about this leader's data..."
                  value={question}
                  onChange={(e) => setQuestion(e.target.value)}
                />
                <div className="chat-actions">
                  <button onClick={() => handleAskAi()} disabled={loading}>Ask AI</button>
                  <button onClick={handleClearChat} disabled={loading || conversation.length === 0}>Clear Chat</button>
                </div>
              </div>
            </>
          ) : (
            <div className="no-leader-selected">
              <p>Select a leader from the list to load 360 data and begin AI analysis.</p>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default AdminAIDashboard;